import uiSchemaDisabledEdit from '../uiSchema/uiSchemaDisabled.json';
import uiSchemaFullEdit from '../uiSchema/uiSchemaFullEdit.json';
import uiSchemaImobiliariaDigital from '../uiSchema/uiSchemaImobiliariaDigital.json';
import uiSchemaImobiliariaDigitalNonPriceable from '../uiSchema/uiSchemaImobiliariaDigitalNonPriceable.json';
import uiSchemaNew from '../uiSchema/uiSchemaNew.json';
import uiSchemaPartialEdit from '../uiSchema/uiSchemaNonPriceableEdit.json';

const resolveRef = (schema, node) => {
  if (!node?.$ref) return node;
  const defName = node.$ref.split('/').pop();
  return schema?.definitions?.[defName] || node;
};

const hiddenField = (fieldUiSchema = {}) => ({
  ...fieldUiSchema,
  'ui:widget': 'hidden',
  'ui:hidden': true,
});

const hiddenObjectField = (fieldUiSchema = {}, childKey) => ({
  ...hiddenField(fieldUiSchema),
  ...(childKey ? { [childKey]: hiddenField(fieldUiSchema?.[childKey]) } : {}),
});

const hideUiFields = (uiSchema, fieldNames) => {
  fieldNames.forEach((fieldName) => {
    uiSchema[fieldName] = hiddenField(uiSchema[fieldName]);
  });
};

export const generatePartiesInvolvedUi = (partiesInvolved, disabled) => {
  const partiesInvolvedUI = {};
  if (!partiesInvolved?.properties) return partiesInvolvedUI;

  Object.keys(partiesInvolved.properties).forEach((key) => {
    if (partiesInvolved.properties[key].$ref.search('people') === -1) {
      partiesInvolvedUI[key] = {
        'ui:field': 'person',
        'ui:title': partiesInvolved.properties[key].title,
        'ui:disabled': disabled,
      };
    } else {
      partiesInvolvedUI[key] = {
        items: {
          'ui:field': 'people',
          'ui:title': partiesInvolved.properties[key].title,
          'ui:disabled': disabled,
          'ui:options': {
            allowCustomTitle: partiesInvolved.properties[key].allowCustomTitle === true,
          },
        },
      };
    }
  });

  return partiesInvolvedUI;
};

export const generateUiSchema = ({
  status,
  inspectionType,
  clientPanel = false,
  isNewOrder = false,
  schema = {},
  me = null,
  previousOrderId = null,
  previousOrderFile = null,
}) => {
  let uiSchema;
  let disabled = false;

  const effectiveStatus = isNewOrder ? 'NEW' : status;

  if (effectiveStatus === 'NEW') {
    uiSchema = JSON.parse(JSON.stringify(uiSchemaNew));
  } else if (clientPanel) {
    switch (effectiveStatus) {
      case 'WAITING':
      case 'ACCEPTED':
        uiSchema = JSON.parse(JSON.stringify(uiSchemaFullEdit));
        break;
      case 'FINISHED':
        uiSchema = JSON.parse(JSON.stringify(uiSchemaPartialEdit));
        break;
      default:
        uiSchema = JSON.parse(JSON.stringify(uiSchemaDisabledEdit));
        disabled = true;
    }
  } else {
    switch (effectiveStatus) {
      case 'ACCEPTED':
      case 'FINALIZED_BY_DEVICE':
        uiSchema = JSON.parse(JSON.stringify(uiSchemaFullEdit));
        break;
      case 'FINISHED':
        uiSchema = JSON.parse(JSON.stringify(uiSchemaPartialEdit));
        break;
      default:
        uiSchema = JSON.parse(JSON.stringify(uiSchemaDisabledEdit));
        disabled = true;
    }
  }

  if (['visitaDigital', 'fotosDeCaptacao'].includes(inspectionType)) {
    uiSchema = {
      ...uiSchema,
      ...(clientPanel || status === 'FINISHED' ? uiSchemaImobiliariaDigitalNonPriceable : uiSchemaImobiliariaDigital)
    };
  }

  if (clientPanel) {
    uiSchema.clients = hiddenObjectField(uiSchema.clients, 'client');
    // Hide inspection_type for clients — the type is shown as the page/step title, not inside the form body
    uiSchema.inspection_type = hiddenField(uiSchema.inspection_type);
    
    // For clients, previous_inspection is always hidden in the form body.
    // It is handled in a separate step (/:orderType/previous) before the form wizard,
    // and the data is injected programmatically into formData by OrderFormPage.
    uiSchema.previous_inspection = {
      'ui:widget': 'hidden',
      'ui:hidden': true,
      inspection: { 'ui:widget': 'hidden', 'ui:hidden': true },
      file: { 'ui:widget': 'hidden', 'ui:hidden': true },
    };
  } else {
    uiSchema.franchisees = hiddenObjectField(uiSchema.franchisees, 'franchisee');
  }

  const isMarketplace = inspectionType?.startsWith('marketplace_');
  if (isMarketplace) {
    hideUiFields(uiSchema, [
      'inspection_type',
      'additionalCosts',
      'tags',
      'attachments',
      'id',
      'orderId',
      'status',
      'type',
    ]);
    uiSchema.franchisees = hiddenObjectField(uiSchema.franchisees, 'franchisee');
    if (clientPanel) {
      uiSchema.clients = hiddenObjectField(uiSchema.clients, 'client');
    }

    if (inspectionType === 'marketplace_docusign') {
      uiSchema.recipients = { 'ui:field': 'docuSignSignersField', 'ui:title': 'signatures.title' };
      uiSchema.documents = { 'ui:field': 'docusignFile', 'ui:title': 'documents.title' };
      uiSchema['ui:order'] = clientPanel ? ['documents', 'recipients', '*'] : ['clients', 'documents', 'recipients', '*'];
    }

    if (inspectionType === 'marketplace_bry') {
      uiSchema.signers = { 'ui:field': 'brySignersField', 'ui:title': 'signatures.title' };
      uiSchema.documents = { 'ui:field': 'docusignFile', 'ui:title': 'documents.title' };
      uiSchema.details = { 
        'ui:order': ['envelope_identification', '*'],
        envelope_identification: {
          'ui:widget': 'customStringWidget',
          'ui:title': 'envelope_identification',
          'ui:options': { label: true }
        }
      };
      uiSchema['ui:order'] = clientPanel ? ['details', 'documents', 'signers', '*'] : ['clients', 'details', 'documents', 'signers', '*'];
    }

    if (inspectionType === 'marketplace_fichacerta') {
      uiSchema['ui:order'] = ['clients', 'desired_property', 'main_suitor', 'main_suitor_address', 'suitors', '*'];
    }
  }

  if (inspectionType === 'tourVirtual') {
    uiSchema = {
      ...uiSchema,
      'ui:order': [
        'inspection_type',
        'franchisees',
        'clients',
        'details',
        'address',
        'accessInformation',
        'accompaniedInspection',
        'tourAccessInfo',
        '*',
        'recommendation',
      ],
    };
  }

  if (inspectionType === 'acompanhamentoObraMM') {
    hideUiFields(uiSchema, clientPanel
      ? ['additionals', 'accessInformation']
      : ['keys', 'cost', 'additionals', 'accessInformation', 'accompaniedInspection', 'recommendation']);
  } else {
    hideUiFields(uiSchema, ['construction_details', 'solicitations']);
  }

  if (inspectionType === 'cautelarQueirozGalvao') {
    hideUiFields(uiSchema, ['accessInformation', 'accompaniedInspection']);
  } else {
    hideUiFields(uiSchema, ['location_references']);
  }

  if (inspectionType === 'cacaVazamento') {
    hideUiFields(uiSchema, [
      'keys',
      'recommendation',
      'accessInformation',
      'tags',
    ]);
    if (uiSchema?.additionals?.urgency) {
      uiSchema.additionals.urgency = hiddenField(uiSchema.additionals.urgency);
    }
    if (uiSchema?.additionals?.modality) {
      uiSchema.additionals.modality = {
        ...uiSchema.additionals.modality,
        'ui:options': {
          ...uiSchema.additionals?.modality?.['ui:options'],
          label: 'Vistoria completa',
          labelId: 'completeInspection',
        },
      };
    }
    if (uiSchema?.details) {
      hideUiFields(uiSchema.details, [
        'area',
        'furnished',
        'purpose',
        'capture_schedule_date',
        'allowed_at_date',
      ]);
    }
  }

  if (
    (me?.profile?.franchisee_type === 'OWNED' || [244, 187].includes(me?.profile?.franchisee_id)) &&
    uiSchema?.additionals?.urgency
  ) {
    uiSchema.additionals.urgency = hiddenField(uiSchema.additionals.urgency);
  }

  const mapAddressField = (obj, showLabel = false, nested = false) => {
    if (obj) {
      obj['ui:field'] = 'addressField';
      obj['ui:disabled'] = disabled || obj['ui:disabled'] === true;
      obj['ui:options'] = { ...obj['ui:options'], label: showLabel, nested };
      obj['ui:order'] = ['zipcode', 'state', 'city', 'neighborhood', 'street', 'number', 'complement'];
    }
  };

  if (uiSchema.address) mapAddressField(uiSchema.address, true, false);
  if (uiSchema.main_suitor_address) {
    mapAddressField(uiSchema.main_suitor_address, true, false);
  }
  if (uiSchema.desired_property?.address) mapAddressField(uiSchema.desired_property.address, false, true);
  if (uiSchema.property_location) mapAddressField(uiSchema.property_location, true, false);

  if (uiSchema.accessInformation) {
    uiSchema.accessInformation['ui:disabled'] = disabled;
  }

  const partiesInvolvedDef = resolveRef(schema, schema?.properties?.parties_involved || schema?.definitions?.parties_involved);
  if (partiesInvolvedDef) {
    uiSchema.parties_involved = {
      ...uiSchema.parties_involved,
      ...generatePartiesInvolvedUi(partiesInvolvedDef, disabled)
    };
  }

  return uiSchema;
};
